import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchDemocracyEngineAccount,
  fetchDemocracyEngineRecipients,
  proxyBrowserDemocracyEngineDonation,
  submitDemocracyEngineDonation
} from '../async/democracy-engine'
import {
  DemocracyEngineAccount,
  DemocracyEngineDonationInput,
  DemocracyEngineRecipient
} from '../types/democracy-engine'

export const useDemocracyEngine = () => {
  const [account, setAccount] = useState<DemocracyEngineAccount>()
  const [recipients, setRecipients] = useState<DemocracyEngineRecipient[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(undefined)
    try {
      const [accountResponse, recipientResponse] = await Promise.all([
        fetchDemocracyEngineAccount(),
        fetchDemocracyEngineRecipients()
      ])
      setAccount(accountResponse)
      setRecipients(Array.isArray(recipientResponse) ? recipientResponse : [])
    } catch (err) {
      setError((err as Error).message || 'Unable to load Democracy Engine data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  return { account, recipients, loading, error, refresh: loadData }
}

export const useDemocracyEngineDonation = () => {
  const { account, recipients, loading, error, refresh } = useDemocracyEngine()
  const [submitting, setSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState<string>()

  const submitBrowserDonation = useCallback(
    async (donation: DemocracyEngineDonationInput) => {
      setSubmitting(true)
      setSubmissionError(undefined)
      try {
        const payload = { donation }
        return await proxyBrowserDemocracyEngineDonation(payload)
      } catch (err) {
        const message = (err as Error).message || 'Donation failed'
        setSubmissionError(message)
        throw err
      } finally {
        setSubmitting(false)
      }
    },
    []
  )

  const submitServerDonation = useCallback(
    async (donation: DemocracyEngineDonationInput) => {
      setSubmitting(true)
      setSubmissionError(undefined)
      try {
        const result = await submitDemocracyEngineDonation({ donation })
        return result
      } catch (err) {
        const message = (err as Error).message || 'Donation failed'
        setSubmissionError(message)
        throw err
      } finally {
        setSubmitting(false)
      }
    },
    []
  )

  const state = useMemo(
    () => ({
      account,
      recipients,
      loading,
      error,
      refresh,
      submitting,
      submissionError,
      submitBrowserDonation,
      submitServerDonation
    }),
    [
      account,
      recipients,
      loading,
      error,
      refresh,
      submitting,
      submissionError,
      submitBrowserDonation,
      submitServerDonation
    ]
  )

  return state
}
